import { CombinedError, useMutation, useQuery } from 'urql';
import {
  CreateSavedViewDocument,
  CreateSavedViewInput,
  DeleteSavedViewDocument,
  GetSavedViewsForProjectDocument,
  SavedView,
  UpdateSavedViewDocument,
  UpdateSavedViewInput,
} from '../gql/graphql';

type UseSavedViewsReturn = {
  savedViews: SavedView[] | null;
  createSavedView(input: CreateSavedViewInput): Promise<SavedView>;
  updateSavedView(
    id: string,
    input: UpdateSavedViewInput['patch']
  ): Promise<void>;
  deleteSavedView(id: string): Promise<void>;
  loading: boolean;
  saving: boolean;
  error: CombinedError | undefined;
};

export const useSavedViews = (
  projectId: string,
  isCommentView: boolean
): UseSavedViewsReturn => {
  const [{ data: savedViewsData, fetching, error }] = useQuery({
    query: GetSavedViewsForProjectDocument,
    variables: { projectId, isCommentView },
  });

  const [createSavedViewData, executeCreateSavedViewMutation] = useMutation(
    CreateSavedViewDocument
  );
  const [updateSavedViewData, executeUpdateSavedViewMutation] = useMutation(
    UpdateSavedViewDocument
  );

  const [deleteSavedViewData, executeDeleteSavedViewMutation] = useMutation(
    DeleteSavedViewDocument
  );

  const createSavedView = async (input: CreateSavedViewInput) => {
    const result = await executeCreateSavedViewMutation({ input });

    if (result.error) {
      throw result.error;
    }

    return result.data?.createSavedView?.savedView as SavedView;
  };

  const updateSavedView = async (
    id: string,
    patch: UpdateSavedViewInput['patch']
  ) => {
    await executeUpdateSavedViewMutation({
      input: {
        id,
        patch,
      },
    });
  };

  const deleteSavedView = async (id: string) => {
    await executeDeleteSavedViewMutation({
      input: {
        id: id,
      },
    });
  };

  return {
    savedViews: (savedViewsData?.savedViews as SavedView[]) ?? null,
    createSavedView,
    updateSavedView,
    deleteSavedView,
    loading: fetching,
    error:
      createSavedViewData.error ??
      updateSavedViewData.error ??
      deleteSavedViewData.error ??
      error,
    saving: createSavedViewData.fetching,
  };
};
